import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Users,
  Target,
  Brain,
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  ShoppingBag,
  MessageSquare,
} from 'lucide-react';
import { clsx } from 'clsx';
import { personasApiV2, type PersonaDetail, type PersonaClient } from '@/services/api';
import { usePersonasStore } from '@/stores/personas';

function formatClientName(client: PersonaClient): string {
  if (client.client_name) return client.client_name;
  const slug = client.slug ?? client.client_uid ?? client.client_id;
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function PersonaDetailPage() {
  const { clientUid, personaSlug } = useParams<{ clientUid: string; personaSlug: string }>();
  const navigate = useNavigate();

  const { clients, fetchClients } = usePersonasStore();

  const [persona, setPersona] = useState<PersonaDetail | null>(null);
  const [basicPersona, setBasicPersona] = useState<PersonaDetail | null>(null);
  const [client, setClient] = useState<PersonaClient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrichedLoading, setEnrichedLoading] = useState(false);

  // Ensure the store has clients (so sidebar/breadcrumb is populated on back nav)
  useEffect(() => {
    if (clients.length === 0) fetchClients();
  }, [clients.length, fetchClients]);

  // Load persona data — resolve clientUid → client_id first, then fetch basic + enriched
  useEffect(() => {
    if (!clientUid || !personaSlug) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setPersona(null);
      setBasicPersona(null);

      // Step 1: resolve the client_id (internal hash) from the clientUid slug
      let resolvedClientId: string = clientUid; // fallback (may work for some APIs)
      try {
        let clientList = clients.length > 0 ? clients : (await personasApiV2.listClients()).clients;
        const found = clientList.find(
          (c) => c.client_uid === clientUid || c.slug === clientUid || c.client_id === clientUid,
        );
        if (found) {
          resolvedClientId = found.client_id;
          setClient(found);
        }
      } catch {
        // if we can't resolve, proceed with slug and hope the API accepts it
      }

      // Step 2: basic list (fast, shows something immediately)
      try {
        const { personas } = await personasApiV2.listPersonas(resolvedClientId);
        const found = personas.find((p) => p.slug === personaSlug || p.id === personaSlug);
        if (found) {
          setBasicPersona(found);
          setPersona(found);
        }
      } catch {
        // not fatal — will try enriched below
      } finally {
        setLoading(false);
      }

      // Step 3: enriched version (slower, has cognitive data)
      setEnrichedLoading(true);
      try {
        const { persona: enriched } = await personasApiV2.getEnrichedPersona(resolvedClientId, personaSlug);
        setPersona(enriched);
        setBasicPersona((prev) => prev ?? enriched);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load persona');
      } finally {
        setEnrichedLoading(false);
      }
    };

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUid, personaSlug]);

  const displayName = persona?.name ?? personaSlug ?? 'Persona';
  const clientName = client ? formatClientName(client) : (clientUid ?? '');

  const stillFetching = loading || enrichedLoading;

  if (stillFetching && !persona) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-slate-400" />
        <span className="ml-3 text-sm text-slate-500">Loading persona…</span>
      </div>
    );
  }

  if (!persona) {
    return (
      <div className="space-y-4">
        <BackLink clientUid={clientUid!} clientName={clientName} />
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error ?? 'Persona not found.'}</span>
        </div>
      </div>
    );
  }

  const isActive = persona.is_active !== false;
  const hasEnriched = !!(
    persona.context_factors?.length ||
    persona.concepts?.length ||
    persona.scenarios?.length
  );

  return (
    <div className="space-y-6 pb-12">
      {/* Back nav */}
      <BackLink clientUid={clientUid!} clientName={clientName} />

      {/* ── Hero header ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-brand-teal to-brand-purple" />
        <div className="px-8 py-6 flex items-start gap-5">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-teal/20 to-brand-purple/20 flex items-center justify-center text-brand-teal flex-shrink-0">
            <User size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-navy-900 leading-tight">{displayName}</h1>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-xs font-mono text-slate-400">{persona.slug ?? persona.id}</span>
                  <span className="text-slate-200">·</span>
                  <Link
                    to={`/personas`}
                    onClick={() => navigate('/personas')}
                    className="text-xs text-brand-teal hover:underline font-medium"
                  >
                    {clientName}
                  </Link>
                  {isActive ? (
                    <span className="flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">
                      <CheckCircle2 size={9} />
                      Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full font-semibold">
                      <XCircle size={9} />
                      Inactive
                    </span>
                  )}
                  {enrichedLoading && (
                    <span className="flex items-center gap-1 text-[10px] text-slate-400">
                      <Loader2 size={10} className="animate-spin" />
                      Enriching…
                    </span>
                  )}
                  {hasEnriched && !enrichedLoading && (
                    <span className="text-[10px] bg-brand-teal/10 text-brand-teal border border-brand-teal/20 px-2 py-0.5 rounded-full font-mono">
                      enriched
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {(persona.description ?? persona.summary_headline) && (
              <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-3xl">
                {persona.description ?? persona.summary_headline}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Summary detail ── */}
      {persona.summary_detail && persona.summary_detail !== persona.description && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-6 py-4">
          <p className="text-sm text-slate-700 leading-relaxed">{persona.summary_detail}</p>
        </div>
      )}

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Decision Factors — 2 cols */}
        {persona.decision_factors && persona.decision_factors.length > 0 && (
          <div className="lg:col-span-2">
            <SectionCard icon={Brain} title="Decision Factors" count={persona.decision_factors.length}>
              <div className="space-y-3">
                {persona.decision_factors.map((df) => (
                  <DecisionFactorRow key={df.name} factor={df} />
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {/* Right column: Demographics + KPIs + Match Criteria */}
        <div className="space-y-4">
          {/* Demographics */}
          {persona.demographics && Object.keys(persona.demographics).length > 0 && (
            <SectionCard icon={Users} title="Demographics">
              <div className="space-y-2">
                {Object.entries(persona.demographics).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-xs font-semibold text-navy-900 text-right max-w-[55%]">{String(val)}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* KPIs */}
          {persona.kpis && persona.kpis.length > 0 && (
            <SectionCard icon={TrendingUp} title="KPIs">
              <div className="flex flex-wrap gap-1.5">
                {persona.kpis.map((kpi) => (
                  <span
                    key={kpi}
                    className="text-[10px] bg-brand-teal/10 text-brand-teal-dark px-2 py-0.5 rounded-full border border-brand-teal/20 font-medium"
                  >
                    {kpi}
                  </span>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Positioning */}
          {(persona.positioning ?? persona.positioning_statement) && (
            <SectionCard icon={Target} title="Positioning">
              <p className="text-sm text-slate-700 leading-relaxed">
                {persona.positioning ?? persona.positioning_statement}
              </p>
            </SectionCard>
          )}

          {/* Match Criteria */}
          {persona.match_criteria && persona.match_criteria.length > 0 && (
            <SectionCard icon={Target} title="Match Criteria">
              <div className="flex flex-wrap gap-1.5">
                {persona.match_criteria.map((mc) => (
                  <span
                    key={mc.concept}
                    title={mc.detail}
                    className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full border border-indigo-200 font-medium cursor-help"
                  >
                    {mc.display}
                  </span>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      {/* ── Cognitive layer (enriched) ── */}
      {hasEnriched && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">Cognitive Layer</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Context Factors */}
            {persona.context_factors && persona.context_factors.length > 0 && (
              <SectionCard icon={Brain} title="Context Factors" count={persona.context_factors.length}>
                <div className="space-y-1.5">
                  {persona.context_factors.map((f, i) => (
                    <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100 leading-relaxed">
                      {f}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Concepts */}
            {persona.concepts && persona.concepts.length > 0 && (
              <SectionCard icon={Sparkles} title="Concepts" count={persona.concepts.length}>
                <div className="flex flex-wrap gap-1.5">
                  {persona.concepts.map((c) => (
                    <span
                      key={c}
                      className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full border border-indigo-200 font-medium"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Buyer Scenarios */}
            {persona.scenarios && persona.scenarios.length > 0 && (
              <SectionCard icon={ShoppingBag} title="Buyer Scenarios" count={persona.scenarios.length}>
                <div className="space-y-1.5">
                  {persona.scenarios.map((s, i) => (
                    <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100 leading-relaxed">
                      {s}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}
          </div>
        </>
      )}

      {/* Actions strip */}
      <div className="bg-white rounded-xl border border-slate-200 px-6 py-4 flex items-center gap-3">
        <MessageSquare size={14} className="text-slate-400" />
        <span className="text-xs text-slate-500">Ask deeper questions about this persona in</span>
        <Link
          to={`/studio`}
          className="text-xs text-brand-teal font-semibold hover:underline"
        >
          Creative Studio →
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Back link
// ---------------------------------------------------------------------------

function BackLink({ clientUid, clientName }: { clientUid: string; clientName: string }) {
  return (
    <Link
      to="/personas"
      className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-navy-900 transition-colors group"
    >
      <ArrowLeft size={13} className="group-hover:-translate-x-0.5 transition-transform" />
      <span>{clientName || 'Personas'}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Section Card
// ---------------------------------------------------------------------------

function SectionCard({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: React.ElementType;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon size={12} className="text-slate-500" />
        </div>
        <h3 className="text-xs font-bold text-navy-900 uppercase tracking-wide">{title}</h3>
        {count !== undefined && (
          <span className="ml-auto text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decision Factor Row
// ---------------------------------------------------------------------------

function DecisionFactorRow({
  factor,
}: {
  factor: { name: string; display: string; summary: string; detail?: string; importance?: number };
}) {
  const pct = factor.importance != null ? Math.round(factor.importance * 100) : null;

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h4 className="text-sm font-semibold text-navy-900">{factor.display}</h4>
        {pct != null && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-brand-teal to-brand-purple rounded-full"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-slate-500 w-7 text-right">{pct}%</span>
          </div>
        )}
      </div>
      {factor.summary && (
        <p className="text-xs text-slate-600 leading-relaxed">{factor.summary}</p>
      )}
      {factor.detail && factor.detail !== factor.summary && (
        <p className="text-xs text-slate-500 leading-relaxed mt-1.5 border-t border-slate-100 pt-1.5">{factor.detail}</p>
      )}
    </div>
  );
}
